import { useEffect, useState, useCallback, useRef } from "react"
import styled from "styled-components"
import AdminService from "../../../services/admin.service"

interface Session {
    session_id: string; nombre?: string; correo?: string; paso_actual: number;
    requiere_humano: boolean; motivo_escalada?: string; escalada_en?: string;
    atendida_por?: string; fecha_ultimo_msg?: string;
}
interface Msg { role: string; nombre_agente?: string; contenido: string; timestamp?: string }

export default function Conversaciones() {
    const [sessions, setSessions] = useState<Session[]>([])
    const [soloPend, setSoloPend] = useState(true)
    const [loading, setLoading] = useState(false)
    const [sel, setSel] = useState<Session | null>(null)
    const [conv, setConv] = useState<Msg[]>([])
    const [convLoading, setConvLoading] = useState(false)
    const [mensaje, setMensaje] = useState("")
    const [remarks, setRemarks] = useState("")
    const [busy, setBusy] = useState(false)
    const chatRef = useRef<HTMLDivElement>(null)
    useEffect(() => { chatRef.current?.scrollTo({ top: chatRef.current.scrollHeight }) }, [conv])

    const load = useCallback(async () => {
        setLoading(true)
        try { const { data } = await AdminService.getBotSessions(soloPend); setSessions(Array.isArray(data) ? data : []) }
        catch { setSessions([]) } finally { setLoading(false) }
    }, [soloPend])

    useEffect(() => { load() }, [load])
    useEffect(() => { const t = setInterval(load, 20000); return () => clearInterval(t) }, [load])

    const openConv = useCallback(async (s: Session) => {
        setSel(s); setConvLoading(true)
        try { const { data } = await AdminService.getBotConversation(s.session_id); setConv(Array.isArray(data) ? data : []) }
        catch { setConv([]) } finally { setConvLoading(false) }
    }, [])

    const responder = async () => {
        if (!sel || !mensaje.trim()) return
        setBusy(true)
        try { await AdminService.botResponder(sel.session_id, mensaje.trim()); setMensaje(""); await openConv(sel) }
        catch {} finally { setBusy(false) }
    }
    const finalizar = async () => {
        if (!sel) return
        setBusy(true)
        try { await AdminService.botFinalizar(sel.session_id, undefined, remarks.trim() || undefined); setRemarks(""); await load(); await openConv({ ...sel, requiere_humano: false }) }
        catch {} finally { setBusy(false) }
    }
    const archivar = async (e: React.MouseEvent, session_id: string) => {
        e.stopPropagation()
        setSessions(prev => prev.filter(s => s.session_id !== session_id))
        if (sel?.session_id === session_id) { setSel(null); setConv([]) }
        try { await AdminService.archivarBotSesion(session_id) } catch { load() }
    }

    const fecha = (d?: string) => d ? new Date(d).toLocaleString("es-CL", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : ""

    return (
        <Wrap>
            <List>
                <ListHead>
                    <label><input type="checkbox" checked={soloPend} onChange={e => setSoloPend(e.target.checked)} /> Solo pendientes</label>
                    <button onClick={load} title="Refrescar">&#8635;</button>
                </ListHead>
                {loading && <Empty>Cargando…</Empty>}
                {!loading && sessions.length === 0 && <Empty>Sin conversaciones</Empty>}
                {sessions.map(s => (
                    <Card key={s.session_id} className={sel?.session_id === s.session_id ? "sel" : ""} onClick={() => openConv(s)}>
                        <div className="top">
                            <strong>{s.nombre || "Anónimo"}</strong>
                            <span className="paso">Paso {s.paso_actual}</span>
                        </div>
                        <div className="sub">{s.correo || "—"}</div>
                        {s.requiere_humano && <div className="alert">⚠ {s.motivo_escalada || "Pendiente de intervención"}</div>}
                        <div className="meta">{fecha(s.fecha_ultimo_msg)}{s.atendida_por ? ` · ${s.atendida_por}` : ""}</div>
                        <button className="arch" title="Archivar conversación" onClick={(e) => archivar(e, s.session_id)}>Archivar</button>
                    </Card>
                ))}
            </List>
            <Detail>
                {!sel && <Empty>Seleccioná una conversación</Empty>}
                {sel && (
                    <>
                        <DetailHead>
                            <div><strong>{sel.nombre || "Anónimo"}</strong> · {sel.correo || "—"}</div>
                            {sel.requiere_humano ? <Badge className="pend">Pendiente</Badge> : <Badge>Bot atendiendo</Badge>}
                        </DetailHead>
                        <Chat ref={chatRef}>
                            {convLoading && <Empty>Cargando…</Empty>}
                            {conv.map((m, i) => (
                                <Bubble key={i} className={m.role}>
                                    {m.role === "human_agent" && <em>{m.nombre_agente || "Agente"}</em>}
                                    <span>{m.contenido}</span>
                                </Bubble>
                            ))}
                        </Chat>
                        <Intervene>
                            <textarea placeholder="Responder como agente (se muestra al pasajero con tu nombre)…" value={mensaje} onChange={e => setMensaje(e.target.value)} />
                            <div className="row">
                                <button disabled={busy || !mensaje.trim()} onClick={responder}>Responder</button>
                                <input placeholder="Remarks operativos (opcional)" value={remarks} onChange={e => setRemarks(e.target.value)} />
                                <button className="fin" disabled={busy} onClick={finalizar}>Finalizar intervención</button>
                            </div>
                        </Intervene>
                    </>
                )}
            </Detail>
        </Wrap>
    )
}

const Wrap = styled.div`display:flex;gap:14px;height:100%;overflow:hidden;`
const List = styled.div`width:300px;flex-shrink:0;overflow-y:auto;display:flex;flex-direction:column;gap:8px;`
const ListHead = styled.div`
display:flex;justify-content:space-between;align-items:center;font-size:13px;color:#8B7355;
label{display:flex;gap:6px;align-items:center;cursor:pointer;}
button{border:none;background:#f2ebe0;border-radius:8px;width:28px;height:28px;cursor:pointer;font-size:15px;}
`
const Card = styled.div`
border:1px solid #e3ddd2;border-radius:10px;padding:10px 12px;cursor:pointer;background:#fff;
&.sel{border-color:#C4622D;box-shadow:0 0 0 1px #C4622D;}
.top{display:flex;justify-content:space-between;align-items:center;}
.top strong{font-size:14px;color:#2D2318;}
.paso{font-size:11px;background:#f2ebe0;color:#8B7355;padding:2px 8px;border-radius:10px;}
.sub{font-size:12px;color:#8B7355;margin-top:2px;}
.alert{font-size:12px;color:#9E4A1E;margin-top:6px;}
.meta{font-size:11px;color:#A09070;margin-top:6px;}
.arch{margin-top:8px;border:none;background:#f2ebe0;color:#8B7355;border-radius:8px;padding:4px 10px;cursor:pointer;font-size:11px;}
.arch:hover{background:#e8ddc9;}
`
const Detail = styled.div`flex:1;display:flex;flex-direction:column;border:1px solid #e3ddd2;border-radius:10px;overflow:hidden;`
const DetailHead = styled.div`display:flex;justify-content:space-between;align-items:center;padding:10px 14px;border-bottom:1px solid #e3ddd2;font-size:13px;color:#2D2318;`
const Badge = styled.span`font-size:11px;padding:3px 10px;border-radius:10px;background:#e2efda;color:#3c6b2f;&.pend{background:#fce4d6;color:#9E4A1E;}`
const Chat = styled.div`flex:1;overflow-y:auto;padding:14px;display:flex;flex-direction:column;gap:8px;background:#faf7f1;`
const Bubble = styled.div`
max-width:78%;padding:8px 12px;border-radius:12px;font-size:13px;line-height:1.5;white-space:pre-wrap;
em{display:block;font-size:11px;color:#9E4A1E;margin-bottom:2px;font-style:normal;font-weight:600;}
&.user{align-self:flex-end;background:#C4622D;color:#fff;}
&.assistant{align-self:flex-start;background:#fff;border:1px solid #e3ddd2;color:#2D2318;}
&.human_agent{align-self:flex-start;background:#fff4ec;border:1px solid #f0c9b0;color:#2D2318;}
&.system{align-self:center;background:#eee;color:#666;font-size:12px;}
`
const Intervene = styled.div`
border-top:1px solid #e3ddd2;padding:10px 12px;display:flex;flex-direction:column;gap:8px;background:#fff;
textarea{width:100%;min-height:48px;resize:vertical;border:1px solid #d9cfbf;border-radius:8px;padding:8px;font-family:inherit;font-size:13px;box-sizing:border-box;}
.row{display:flex;gap:8px;align-items:center;}
.row input{flex:1;border:1px solid #d9cfbf;border-radius:8px;padding:8px;font-size:13px;}
.row button{border:none;border-radius:8px;padding:9px 14px;background:#C4622D;color:#fff;cursor:pointer;font-size:13px;}
.row button.fin{background:#8B7355;}
.row button:disabled{opacity:.5;cursor:default;}
`
const Empty = styled.div`color:#A09070;font-size:13px;text-align:center;padding:24px;margin:auto;`
